import apiClient from './client';

const V2_BASE = '/api/v2';
const ROOT = '/personas';

export type PersonaStatus = 'ACTIVE' | 'DEPRECATED';

export interface PersonaNode {
  id: string;
  canonical_label: string;
  status: PersonaStatus;
  created_at: string;
  updated_at: string;
}

export interface PersonaAlias {
  id: string;
  surface_form: string;
  node_id: string;
  status: PersonaStatus;
  assigned_by: string | null;
  created_at: string;
}

export interface SuggestedPersonaNode {
  node_id: string;
  label: string;
  reason: string;
}

/** A custom archetype surface awaiting curation. */
export interface UnresolvedPersonaItem {
  surface: string;
  occurrence_count: number;
  first_seen: string;
  last_seen: string;
  source_types: string[];
  original_texts: string[];
}

export interface AliasOnDeprecatedNodeItem {
  alias_id: string;
  surface_form: string;
  node_id: string;
  node_label: string | null;
}

export interface PersonaCurationQueue {
  orphans: UnresolvedPersonaItem[];
  active_aliases_on_deprecated_nodes: AliasOnDeprecatedNodeItem[];
}

export interface OrphanResolution {
  node_id: string;
  alias_id: string;
  cleared: number;
}

// ---- Reads ----

export async function fetchQueue(): Promise<PersonaCurationQueue> {
  const response = await apiClient.get<PersonaCurationQueue>(`${ROOT}/queue`, { baseURL: V2_BASE });
  return response.data;
}

export async function fetchNodes(): Promise<PersonaNode[]> {
  const response = await apiClient.get<PersonaNode[]>(`${ROOT}/nodes`, { baseURL: V2_BASE });
  return response.data;
}

export async function fetchAliases(): Promise<PersonaAlias[]> {
  const response = await apiClient.get<PersonaAlias[]>(`${ROOT}/aliases`, { baseURL: V2_BASE });
  return response.data;
}

export async function fetchSuggestions(surface: string): Promise<SuggestedPersonaNode[]> {
  const response = await apiClient.get<SuggestedPersonaNode[]>(`${ROOT}/suggestions`, {
    baseURL: V2_BASE,
    params: { surface },
  });
  return response.data;
}

// ---- Orphan resolution ----

export async function linkSurface(surface: string, nodeId: string): Promise<OrphanResolution> {
  const response = await apiClient.post<OrphanResolution>(
    `${ROOT}/orphans/link`, { surface, node_id: nodeId }, { baseURL: V2_BASE });
  return response.data;
}

export async function mintForSurface(surface: string, label: string): Promise<OrphanResolution> {
  const response = await apiClient.post<OrphanResolution>(
    `${ROOT}/orphans/mint`, { surface, label }, { baseURL: V2_BASE });
  return response.data;
}

export async function resyncOrphans(): Promise<{ cleared: number }> {
  const response = await apiClient.post<{ cleared: number }>(
    `${ROOT}/orphans/resync`, {}, { baseURL: V2_BASE });
  return response.data;
}

export async function rejectSurface(surface: string): Promise<void> {
  await apiClient.post(`${ROOT}/orphans/reject`, { surface }, { baseURL: V2_BASE });
}

export async function reinstateSurface(surface: string): Promise<void> {
  await apiClient.delete(`${ROOT}/orphans/reject`, { baseURL: V2_BASE, params: { surface } });
}

// ---- Alias operations ----

export async function repointAlias(aliasId: string, nodeId: string): Promise<PersonaAlias> {
  const response = await apiClient.patch<PersonaAlias>(
    `${ROOT}/aliases/${aliasId}/node`, { node_id: nodeId }, { baseURL: V2_BASE });
  return response.data;
}

export async function setAliasActive(aliasId: string, active: boolean): Promise<PersonaAlias> {
  const response = await apiClient.patch<PersonaAlias>(
    `${ROOT}/aliases/${aliasId}/status`, { active }, { baseURL: V2_BASE });
  return response.data;
}

// ---- Node operations ----

export async function relabelNode(nodeId: string, label: string): Promise<PersonaNode> {
  const response = await apiClient.patch<PersonaNode>(
    `${ROOT}/nodes/${nodeId}/label`, { label }, { baseURL: V2_BASE });
  return response.data;
}

export async function setNodeActive(nodeId: string, active: boolean): Promise<PersonaNode> {
  const response = await apiClient.patch<PersonaNode>(
    `${ROOT}/nodes/${nodeId}/status`, { active }, { baseURL: V2_BASE });
  return response.data;
}
